import { useContext, useEffect } from "react";
import { useImmer } from "use-immer";
import qs from "qs";
import moment from "moment";
import { useDebouncedCallback } from "use-debounce";
import { errorMessage, successMessage } from "../../../utils";
import {
  DownloadCouponReportList,
  getCouponReport,
} from "../../CouponReport/api";
import { AppContext } from "../../../store";

export const useBirthdayCouponUsageHistoryV2 = ({ couponId }) => {
  const { appState } = useContext(AppContext);
  const {
    currentTabs: { birthdayCouponsV2: currentTab },
  } = appState;

  const [state, setState] = useImmer({
    isLoading: false,
    usageHistory: {
      data: [],
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        pageCount: 0,
        total: 0,
        hasMorePages: true,
        lastPage: 1,
      },
      sortBy: [],
    },
    usageHistoryFilters: {
      searchText: "",
      stores: [],
      date: {},
      all: [],
    },
    isCalenderOpen: false,
    date: [
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ],
    showDate: [],
    disableApplyButton: true,
  });

  const getUsageHistoryList = async ({
    searchText = "",
    stores = [],
    date = {},
    pagination,
    sortBy = [],
  }) => {
    if (!couponId) {
      return;
    }

    let params = {};

    if (searchText) {
      params = { ...params, search: searchText };
    }

    params = { ...params, coupon_id: [couponId] };

    if (Object.keys(date).length > 0) {
      params = {
        ...params,
        start_date: moment(date.value.startDate).format("YYYY-MM-DD"),
        end_date: moment(date.value.endDate).format("YYYY-MM-DD"),
      };
    }

    const storeIDs = stores.map((store) => store.value);
    if (storeIDs.length > 0) {
      params = {
        ...params,
        store_id: storeIDs,
      };
    }

    if (sortBy.length > 0) {
      const sortByParams = sortBy.reduce(
        (obj, item) =>
          Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
        {},
      );
      params = {
        ...params,
        sort: sortByParams,
      };
    }

    const query = {
      params,
      paramsSerializer: (queryParams) => qs.stringify(queryParams),
    };

    try {
      setState((draft) => {
        draft.isLoading = true;
      });
      console.log(pagination, query);

      const res = await getCouponReport(query, pagination);
      console.log(res);
      
      if (res.success) {
        setState((draft) => {
          draft.usageHistory.data = res.data.coupon_history;
          draft.usageHistory.pagination = res.data.pagination;
          draft.isLoading = false;
        });
      }
    } catch (err) {
      errorMessage(
        err.response?.data?.message || "Failed to fetch usage history",
      );
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const debouncedSearch = useDebouncedCallback((value) => {
    getUsageHistoryList({
      searchText: value,
      stores: state.usageHistoryFilters.stores,
      date: state.usageHistoryFilters.date,
      pagination: {
        pageIndex: 1,
        pageSize: state.usageHistory.pagination.pageSize,
      },
      sortBy: state.usageHistory.sortBy,
    });
  }, 1000);

  const onChangeUsageHistoryFilter = (e) => {
    const { name, value } = e.target;

    setState((draft) => {
      draft.usageHistoryFilters[name] = value;
    });

    debouncedSearch(value);
  };

  const clearSearchUsageHistoryFilter = () => {
    setState((draft) => {
      draft.usageHistoryFilters.searchText = "";
    });

    getUsageHistoryList({
      searchText: "",
      stores: state.usageHistoryFilters.stores,
      date: state.usageHistoryFilters.date,
      pagination: {
        pageIndex: 1,
        pageSize: state.usageHistory.pagination.pageSize,
      },
      sortBy: state.usageHistory.sortBy,
    });
  };

  const onChangeDate = (value) => {
    setState((draft) => {
      draft.date = value;
      draft.disableApplyButton = false;
    });
  };

  const handleIsOpen = (isOpen) => {
    setState((draft) => {
      draft.isCalenderOpen = isOpen;
    });
  };

  const rebuildAppliedFilters = (stores, date) => {
    const appliedFilters = [];
    if (stores.length > 0) {
      stores.forEach((store) => {
        appliedFilters.push({
          value: store.value,
          type: "Store",
          label: store.label,
        });
      });
    }
    if (Object.keys(date).length > 0) {
      appliedFilters.push({
        value: date.value,
        type: "Date",
        label: date.label,
      });
    }
    return appliedFilters;
  };

  const handleDateChangeApply = () => {
    const formattedDate = {
      value: state.date[0],
      label: `${moment(state.date[0].startDate).format("yyyy-MM-DD")} to ${moment(
        state.date[0].endDate,
      ).format("yyyy-MM-DD")}`,
      type: "Date",
    };

    const updatedFilters = rebuildAppliedFilters(
      state.usageHistoryFilters.stores,
      formattedDate,
    );

    setState((draft) => {
      draft.showDate = state.date;
      draft.isCalenderOpen = false;
      draft.usageHistoryFilters.date = formattedDate;
      draft.usageHistoryFilters.all = updatedFilters;
      draft.disableApplyButton = false;
    });
  };

  const handleStoreFilter = (_, stores) => {
    const storesWithType = stores.map((store) => ({
      ...store,
      type: "Store",
    }));
    const updatedFilters = rebuildAppliedFilters(
      storesWithType,
      state.usageHistoryFilters.date,
    );

    setState((draft) => {
      draft.usageHistoryFilters.stores = storesWithType;
      draft.usageHistoryFilters.all = updatedFilters;
      draft.disableApplyButton = false;
    });
  };

  const handleApplyFilter = () => {
    const appliedFilters = [];
    if (state.usageHistoryFilters.stores.length > 0) {
      state.usageHistoryFilters.stores.forEach((store) => {
        appliedFilters.push({
          value: store.value,
          type: "Store",
          label: store.label,
        });
      });
    }
    if (Object.keys(state.usageHistoryFilters.date).length > 0) {
      appliedFilters.push({
        value: state.usageHistoryFilters.date.value,
        type: "Date",
        label: state.usageHistoryFilters.date.label,
      });
    }

    getUsageHistoryList({
      searchText: state.usageHistoryFilters.searchText,
      stores: state.usageHistoryFilters.stores,
      date: state.usageHistoryFilters.date,
      pagination: {
        pageIndex: 1,
        pageSize: state.usageHistory.pagination.pageSize,
      },
      sortBy: state.usageHistory.sortBy,
    });

    setState((draft) => {
      draft.usageHistoryFilters.all = appliedFilters;
      draft.disableApplyButton = true;
    });
  };

  const handleFilterClear = (clearType, value) => {
    if (clearType === "single") {
      let updatedStores = state.usageHistoryFilters.stores;
      let updatedDate = state.usageHistoryFilters.date;

      if (value.type === "Store") {
        updatedStores = state.usageHistoryFilters.stores.filter(
          (store) => store.value !== value.value,
        );
      }

      if (value.type === "Date") {
        updatedDate = {};
      }

      // Rebuild the all array with remaining filters
      const remainingFilters = [];
      if (updatedStores.length > 0) {
        updatedStores.forEach((store) => {
          remainingFilters.push({
            value: store.value,
            type: "Store",
            label: store.label,
          });
        });
      }
      if (Object.keys(updatedDate).length > 0) {
        remainingFilters.push({
          value: updatedDate.value,
          type: "Date",
          label: updatedDate.label,
        });
      }

      setState((draft) => {
        draft.usageHistoryFilters.stores = updatedStores;
        draft.usageHistoryFilters.date = updatedDate;
        draft.usageHistoryFilters.all = remainingFilters;
        if (value.type === "Date") {
          draft.showDate = [];
        }
        draft.disableApplyButton = false;
      });
    } else if (clearType === "all") {
      setState((draft) => {
        draft.usageHistoryFilters.all = [];
        draft.usageHistoryFilters.stores = [];
        draft.usageHistoryFilters.date = {};
        draft.showDate = [];
        draft.disableApplyButton = true;
      });

      getUsageHistoryList({
        searchText: state.usageHistoryFilters.searchText,
        stores: [],
        date: {},
        pagination: {
          pageIndex: 1,
          pageSize: state.usageHistory.pagination.pageSize,
        },
        sortBy: state.usageHistory.sortBy,
      });
    }
  };

  const resetUsageHistoryFilters = () => {
    setState((draft) => {
      draft.usageHistoryFilters.searchText = "";
      draft.usageHistoryFilters.stores = [];
      draft.usageHistoryFilters.date = {};
      draft.showDate = [];
      draft.disableApplyButton = true;
    });

    getUsageHistoryList({
      searchText: "",
      stores: [],
      date: {},
      pagination: {
        pageIndex: 1,
        pageSize: state.usageHistory.pagination.pageSize,
      },
      sortBy: state.usageHistory.sortBy,
    });
  };

  const gotoPageUsageHistory = (pageIndex) => {
    getUsageHistoryList({
      searchText: state.usageHistoryFilters.searchText,
      stores: state.usageHistoryFilters.stores,
      date: state.usageHistoryFilters.date,
      pagination: {
        pageIndex,
        pageSize: state.usageHistory.pagination.pageSize,
      },
      sortBy: state.usageHistory.sortBy,
    });
  };

  const setPageSizeUsageHistory = (e) => {
    getUsageHistoryList({
      searchText: state.usageHistoryFilters.searchText,
      stores: state.usageHistoryFilters.stores,
      date: state.usageHistoryFilters.date,
      pagination: {
        pageIndex: 1,
        pageSize: +e.target.value,
      },
      sortBy: state.usageHistory.sortBy,
    });
  };

  const handleSortUsageHistory = (sortBy) => {
    getUsageHistoryList({
      searchText: state.usageHistoryFilters.searchText,
      stores: state.usageHistoryFilters.stores,
      date: state.usageHistoryFilters.date,
      pagination: {
        pageIndex: 1,
        pageSize: state.usageHistory.pagination.pageSize,
      },
      sortBy,
    });

    setState((draft) => {
      draft.usageHistory.sortBy = sortBy;
    });
  };

  const handleDownloadUsageHistory = async () => {
    if (!couponId) {
      return;
    }

    try {
      const { searchText, stores, date } = state.usageHistoryFilters;
      const storeIDs = stores.map((store) => store.value);

      let params = {
        coupon_id: [couponId],
      };

      if (searchText) {
        params = {
          ...params,
          search: searchText,
        };
      }

      if (Object.keys(date).length > 0) {
        params = {
          ...params,
          start_date: moment(date.value.startDate).format("YYYY-MM-DD"),
          end_date: moment(date.value.endDate).format("YYYY-MM-DD"),
        };
      }

      if (storeIDs.length > 0) {
        params = {
          ...params,
          store_id: storeIDs,
        };
      }

      const query = {
        params,
        paramsSerializer: (queryParams) =>
          qs.stringify(queryParams, { encode: true }),
      };

      const res = await DownloadCouponReportList(query);

      if (res.success) {
        successMessage(res.message);
      } else {
        errorMessage(res.message);
      }
    } catch (err) {
      errorMessage(
        err.response?.data?.message || "Failed to export usage history",
      );
    }
  };

  useEffect(() => {
    if (couponId && currentTab === "3") {
      getUsageHistoryList({
        searchText: state.usageHistoryFilters.searchText,
        stores: state.usageHistoryFilters.stores,
        date: state.usageHistoryFilters.date,
        pagination: {
          pageIndex: 1,
          pageSize: state.usageHistory.pagination.pageSize,
        },
        sortBy: state.usageHistory.sortBy,
      });
    }
  }, [couponId, currentTab]);

  return {
    state,
    onChangeUsageHistoryFilter,
    onChangeDate,
    clearSearchUsageHistoryFilter,
    handleDateChangeApply,
    handleIsOpen,
    handleStoreFilter,
    handleApplyFilter,
    handleFilterClear,
    resetUsageHistoryFilters,
    gotoPageUsageHistory,
    setPageSizeUsageHistory,
    handleSortUsageHistory,
    handleDownloadUsageHistory,
  };
};
